/**
 * Exposure analysis (MathToolImplementation.md §6.3 `exposure`; §3.2 `usedSubsetDeclared`).
 *
 * A claim's *exposure* is the set of constraints it depends on whose status is
 * merely `adopted-interpretive` — soft, interpretive assumptions rather than
 * `adopted-formal` commitments. These are the points where the claim is most
 * exposed: if an interpretive constraint is later challenged or re-read, the
 * claim's standing can move. Agent 4 surfaces this through the L3
 * `exposure(claimId)` query; this is the pure engine behind it.
 *
 * "Depends on" is taken as the constraints the claim *touches*: its required base
 * (`needs`) together with the constraints it `violates` (the ones whose activation
 * would refute it). The result is filtered to those currently registered as
 * `adopted-interpretive`, deterministically ordered (needs first, then violates),
 * and de-duplicated by first appearance — so the order never depends on Map
 * iteration order (INV-DET).
 */
/**
 * The interpretive-constraint exposure of a claim. Returns the constraint ids the
 * claim depends on whose registered status is `adopted-interpretive`. An unknown
 * claim, or one with no interpretive dependencies, yields an empty array. This is
 * the shape Agent 4's L3 `exposure(claimId)` query returns (§6.3).
 */
export function exposure(state, claimId) {
    const claim = state.claims.get(claimId);
    if (claim === undefined)
        return Object.freeze([]);
    const seen = new Set();
    const exposed = [];
    // Fixed iteration order over the claim's own (ordered) arrays keeps this pure
    // and deterministic regardless of the constraints-map insertion order.
    for (const id of [...claim.needs, ...claim.violates]) {
        if (seen.has(id))
            continue;
        seen.add(id);
        if (state.constraints.get(id)?.status === 'adopted-interpretive')
            exposed.push(id);
    }
    return Object.freeze(exposed);
}
export function exposureReport(state, claimId) {
    const claim = state.claims.get(claimId);
    if (claim === undefined) {
        return Object.freeze({ claimId, dependencies: Object.freeze([]), exposed: Object.freeze([]) });
    }
    const seen = new Set();
    const dependencies = [];
    const exposed = [];
    const roles = [
        [claim.needs, 'needs'],
        [claim.violates, 'violates'],
    ];
    for (const [ids, role] of roles) {
        for (const id of ids) {
            if (seen.has(id))
                continue;
            seen.add(id);
            const status = state.constraints.get(id)?.status;
            const interpretive = status === 'adopted-interpretive';
            dependencies.push(Object.freeze({ id, role, status, interpretive }));
            if (interpretive)
                exposed.push(id);
        }
    }
    return Object.freeze({
        claimId,
        dependencies: Object.freeze(dependencies),
        exposed: Object.freeze(exposed),
    });
}
//# sourceMappingURL=exposure.js.map